import React, { useState, useEffect, useCallback } from "react";
import { Outlet, Navigate, Link } from "react-router-dom";
import { List, Flask } from "@phosphor-icons/react";
import Sidebar from "./Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function AppLayout() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [testMode, setTestMode] = useState(null); // {enabled, note}

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/campaign-settings");
      setTestMode({ enabled: !!data.test_mode_enabled, note: data.test_mode_note || null });
    } catch {
      setTestMode(null);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshSettings();
    const onUpd = () => refreshSettings();
    window.addEventListener("campaign-settings-updated", onUpd);
    // Poll every 60s so other pages pick up settings changes made elsewhere
    const t = setInterval(refreshSettings, 60_000);
    return () => {
      window.removeEventListener("campaign-settings-updated", onUpd);
      clearInterval(t);
    };
  }, [user, refreshSettings]);

  if (user === undefined) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#0B0F19]">
        <div className="text-gray-400 font-display text-sm">Loading...</div>
      </div>
    );
  }
  if (user === null) return <Navigate to="/login" replace />;

  return (
    <div className="h-screen flex overflow-hidden bg-[#0B0F19]">
      {/* desktop sidebar */}
      <div className="hidden lg:block w-64 flex-shrink-0">
        <Sidebar />
      </div>
      {/* mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="relative w-64 h-full">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="lg:hidden h-14 px-4 flex items-center justify-between bg-[#0B0F19]/90 backdrop-blur-xl border-b border-white/10 z-10">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="text-gray-300 hover:text-white"
          >
            <List size={22} />
          </button>
          <span className="font-display font-bold text-sm text-white">MBE Road To Bali</span>
          <span className="w-6" />
        </header>
        {testMode?.enabled && (
          <div
            data-testid="test-mode-banner"
            className="px-4 py-2 bg-gradient-to-r from-orange-600/30 via-orange-500/25 to-amber-500/20 border-b border-orange-500/40 flex items-center gap-3 text-xs text-orange-100"
          >
            <Flask size={16} weight="duotone" className="text-orange-300 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-bold uppercase tracking-wider text-orange-200">Test Mode Active</span>
              <span className="mx-2 text-orange-400/60">·</span>
              <span className="text-orange-100/80">
                Out-of-window trades are accepted and flagged but do NOT count for milestones.
              </span>
              {testMode.note && (
                <>
                  <span className="mx-2 text-orange-400/60">·</span>
                  <span className="italic text-orange-100/70">{testMode.note}</span>
                </>
              )}
            </div>
            {user?.role === "super_admin" && (
              <Link
                to="/settings"
                data-testid="test-mode-banner-link"
                className="hidden sm:inline text-orange-200 hover:text-white underline underline-offset-2"
              >
                Manage
              </Link>
            )}
          </div>
        )}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
